import { useState } from "react";
import { Tooltip, Paper, Skeleton, Grid, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { IApplicationDocument } from "~/api";
import { Timestamp } from "~/components/core/Timestamp";
import { ConfirmDocumentDeleteModal } from "~/components/application/ConfirmDeleteDocumentModal";

export interface ApplicationDocumentCardProps {
  loading?: boolean;
  document?: IApplicationDocument;
  /** Hides the delete button entirely. Use when the user shouldn't be able to delete at all. */
  canDeleteAttachments?: boolean;
  /** Disable the delete button. Use to temporarily disable the button and keep it visible, for e.g. when a delete operation is already pending. */
  disableDelete?: boolean;
  deleteDocument?: (
    contactDocumentId: string,
    filename?: string,
    comments?: string,
  ) => void;
}

export function ApplicationDocumentCard(props: ApplicationDocumentCardProps) {
  const {
    loading,
    document,
    canDeleteAttachments = false,
    deleteDocument = () => {},
    disableDelete = true,
  } = props;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const onDelete = () => {
    deleteDocument(document?.id ?? "", document?.filename, document?.comments);
    setDeleteOpen(false);
  };

  return (
    <Paper
      elevation={1}
      component={Grid}
      container
      wrap="nowrap"
      sx={{
        justifyContent: "space-between",
        px: 2,
        py: 1.5,
        borderRadius: 2,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: (theme) => theme.palette.background.default,
        height: "100%",
      }}
    >
      <Grid item xs zeroMinWidth>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: "uppercase" }}
          noWrap
        >
          {loading ? <Skeleton /> : <Timestamp>{document?.lastModifiedDate}</Timestamp>}
        </Typography>
        <Tooltip title={document?.filename}>
          <Typography variant="body1" fontWeight="bold" noWrap>
            {loading ? <Skeleton /> : document?.filename}
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="secondary">
          {loading ? <Skeleton /> : document?.type?.label}
        </Typography>
      </Grid>
      {!loading && canDeleteAttachments && Boolean(document?.uploadedByAgent) && (
        <>
          <IconButton
            onClick={() => setDeleteOpen(true)}
            disabled={disableDelete}
            sx={{
              position: "relative",
              mr: -0.5,
              right: 0,
              top: 0,
              height: 32,
              width: 32,
            }}
          >
            <DeleteIcon />
          </IconButton>
          <ConfirmDocumentDeleteModal
            open={deleteOpen}
            filename={document?.filename}
            onDelete={onDelete}
            onCancel={() => setDeleteOpen(false)}
          />
        </>
      )}
    </Paper>
  );
}
